import { ParameterForm } from '@/features/parameters/components/organisms/ParameterForm/ParameterForm';
import { useState } from 'react';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { parameterFormSchema, ParameterFormSchemaType } from "@/features/parameters/components/organisms/ParameterForm";
import { Info } from 'lucide-react';
import { Configuration, ConfigurationParameterKey } from '@/types/crm/configuration/configuration';
import { Step3Props } from './Step3.types';


export function Step3({
    defaultData,
    onNext,
    onBack,
    onClose,
}: Step3Props) {

    const defaultValues: Configuration = {
        parameter: {
            crm: [],
            project: [],
            survey: [],
            question: [],
        }
    }
    const form = useForm<ParameterFormSchemaType>({
        resolver: zodResolver(parameterFormSchema),
        defaultValues: defaultData || defaultValues
    });

    const [step, setStep] = useState<ConfigurationParameterKey>("project");

    const onBackHandler = () => {
        switch (step) {
            case "project":
                onBack();
                break;
            case "survey":
                setStep("project");
                break;
            case "question":
                setStep("survey");
                break;
        }
    }

    const onNextHandler = () => {
        switch (step) {
            case "project":
                setStep("survey");
                break;
            case "survey":
                setStep("question");
                break;
            case "question":
                form.handleSubmit((data) => {
                    onNext(data);
                }, (error) => {
                    if (error.parameter?.project) {
                        setStep("project");
                    } else if (error.parameter?.survey) {
                        setStep("survey");
                    } else if (error.parameter?.question) {
                        setStep("question");
                    }
                })();
                break;
        }
    }

    const onTabClickHandler = (tab: ConfigurationParameterKey) => {
        setStep(tab);
    }

    return (
        <>
            <div className="flex items-center my-2 text-blue-600 ">
                <Info className="flex-shrink-0 me-1" size="16" />
                <p className="text-xs">Salesforce parameters are defined based on a particular object’s properties. These object properties can be associated at the project, survey, or question level from Collaborate. Please add all the salesforce properties that will allow your Collaborate Project to connect to your Salesforce Page object.</p>
            </div>

            <Tabs defaultValue="projects" value={step} onValueChange={(value) => { onTabClickHandler(value as ConfigurationParameterKey) }}>
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="project" >Project</TabsTrigger>
                    <TabsTrigger value="survey">Survey</TabsTrigger>
                    <TabsTrigger value="question">Question</TabsTrigger>
                </TabsList>

                <TabsContent value="project">
                    <ParameterForm
                        label="Project"
                        description="Are there data elements associated with this CRM that are needed for all projects?"
                        parameterName={"project"}
                        form={form}
                    />
                </TabsContent>
                <TabsContent value="survey">
                    <ParameterForm
                        label="Surveys"
                        description="Are there data elements associated with this CRM that are needed for all surveys?"
                        parameterName={"survey"}
                        form={form}
                    />
                </TabsContent>
                <TabsContent value="question">
                    <ParameterForm
                        label="Questions"
                        description="Are there data elements associated with this CRM that are needed for all questions?"
                        parameterName={"question"}
                        form={form}
                    />
                </TabsContent>
            </Tabs>

            <div className="flex items-stretch justify-between w-full pt-3 m-0 mt-3 border-t sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>

                <div className="flex m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                    <Button variant="secondary" onClick={onBackHandler}>Back</Button>
                    <Button onClick={onNextHandler}>Next</Button>
                </div>
            </div>
        </>
    )
}


